/**
 * "Do this once the page the visitor is actually looking at is done."
 *
 * Both background jobs on this site — decoding the subpage artwork
 * (`routePrewarm`) and loading the tour-dates widget off-screen (`App`) — must
 * wait for the same two conditions, and must never run twice. One implementation
 * so they cannot drift, and every dependency injectable so the scheduling is
 * testable without a browser.
 */

export type IdleDeps = {
  /** Runs the callback once the document has finished loading. */
  onLoad?: (run: () => void) => void;
  /** Defers to a moment the main thread is not busy. */
  onIdle?: (run: () => void) => void;
};

export function afterLoadThenIdle(run: () => void, deps: IdleDeps = {}): void {
  const { onLoad = defaultOnLoad, onIdle = defaultOnIdle } = deps;
  onLoad(() => onIdle(run));
}

export function defaultOnLoad(run: () => void): void {
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
}

export function defaultOnIdle(run: () => void): void {
  const idle = (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  // Safari has no requestIdleCallback; a timeout is a good enough "not now".
  if (idle) idle(run);
  else setTimeout(run, 200);
}

export type Connection = { saveData?: boolean; effectiveType?: string };

/**
 * Someone on a metered or slow connection did not ask to download a page they
 * may never open. Applies to the artwork and to the third-party widget alike.
 */
export function shouldPreload(connection?: Connection): boolean {
  if (!connection) return true;
  if (connection.saveData) return false;
  return connection.effectiveType !== "2g" && connection.effectiveType !== "slow-2g";
}

export function navigatorConnection(): Connection | undefined {
  return (navigator as { connection?: Connection }).connection;
}
